import PaymentStepNumbers from './PaymentStepNumbers'
import { useEffect, useState } from 'react'
import { paymentsStepsNumbersConfig } from '../../helpers/paymentsStepsNumbersConfig'
import Button from '../Buttons/Button'
import { useTranslation } from 'react-i18next'
import BackBtn from '../Buttons/BackBtn'
import { useFormik } from 'formik'
import PaymentUserDetailsForm from './PaymentUserDetailsForm'
import PaymentBillingDetailsForm from './PaymentBillingDetailsForm'
import { paymentValidationSchema } from '../../helpers/paymentValidationSchema'
import useCreateCheckout, {
  CheckoutPaymentMethod,
  isCardCheckout,
} from '../../services/payment/useCreateCheckout'
import { useTurnstile } from '../../hooks/useTurnstile'
import FormikErrorText from '../Formik/FormikErrorText'
import { ORGANIZATION_TITLE_MAX_LENGTH } from '../../helpers/organizationLimits'
import Spinner from '../UI/Spinner'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import usePricingPlans from '../../services/payment/usePricingPlans'
import ReviewPlan from './ReviewPlan'
import { toast } from 'react-toastify'
import PaymentMethodForm from './PaymentMethodForm'
import BankTransferSentModal from '../Modals/BankTransferSentModal'

const PaymentStepsForms = () => {
  const { t, i18n } = useTranslation()
  const [params] = useSearchParams()
  const paramsPlanId = params.get('planId')
  const storedPlanId = sessionStorage.getItem('planId')
  const planId = paramsPlanId || storedPlanId
  const storedUserDetailsForm = JSON.parse(
    sessionStorage.getItem('userDetailsForm') || '{}',
  )
  const storedBillingDetailsForm = JSON.parse(
    sessionStorage.getItem('billingDetailsForm') || '{}',
  )
  const storedPaymentDetailsForm = JSON.parse(
    sessionStorage.getItem('paymentDetailsForm') || '{}',
  )

  // Sessions started before the backend contract was confirmed persisted
  // 'bank', which the API reads as "not bank_transfer" and silently routes to
  // Stripe. Map it forward rather than stranding a half-finished signup.
  const storedPaymentMethod: CheckoutPaymentMethod =
    storedPaymentDetailsForm?.paymentMethod === 'bank_transfer' ||
    storedPaymentDetailsForm?.paymentMethod === 'bank'
      ? 'bank_transfer'
      : 'card'

  const [activeStep, setActiveStep] = useState<number>(
    JSON.parse(sessionStorage.getItem('paymentActiveStep') || '1'),
  )
  const [isBankTransferModalOpen, setIsBankTransferModalOpen] = useState(false)
  const navigate = useNavigate()

  const { mutate, isLoading } = useCreateCheckout()
  // The account is only created on the last step, so the widget runs there.
  const turnstile = useTurnstile({
    siteKey:
      activeStep === 4 ? process.env.REACT_APP_TURNSTILE_SITE_KEY : undefined,
    action: 'checkout',
    // The widget speaks ISO 639-1; the detector may report 'el-GR'.
    language: i18n.language?.split('-')[0],
  })
  const { data: pricingPlans, isLoading: isPricingPlansLoading } =
    usePricingPlans({
      enabled: !!planId,
    })
  const selectedPlan = pricingPlans?.find((plan) => plan.id === Number(planId))

  const goToStep = (newStep: number) => {
    setActiveStep(newStep)
    sessionStorage.setItem('paymentActiveStep', JSON.stringify(newStep))
  }

  const handleBack = () => goToStep(Math.max(1, activeStep - 1))
  const handleNext = () => goToStep(Math.min(4, activeStep + 1))

  const userDetailsFormik = useFormik({
    initialValues: {
      firstName: storedUserDetailsForm?.firstName ?? '',
      lastName: storedUserDetailsForm?.lastName ?? '',
      username: storedUserDetailsForm?.username ?? '',
      email: storedUserDetailsForm?.email ?? '',
      privacyPolicy: storedUserDetailsForm?.privacyPolicy ?? false,
      termsAndConditions: storedUserDetailsForm?.termsAndConditions ?? false,
      endUserLicenseAgreement:
        storedUserDetailsForm?.endUserLicenseAgreement ?? false,
    },
    validationSchema: paymentValidationSchema.details,
    onSubmit: (values) => {
      sessionStorage.setItem('userDetailsForm', JSON.stringify(values))
      handleNext()
    },
  })

  const billingDetailsFormik = useFormik({
    initialValues: {
      // Clamped rather than merely validated: a value stored before the
      // cap existed would otherwise fail validation with no visible
      // message, since this form shows errors only as a red border.
      companyName: (storedBillingDetailsForm?.companyName ?? '').slice(
        0,
        ORGANIZATION_TITLE_MAX_LENGTH,
      ),
      companyEmail: storedBillingDetailsForm?.companyEmail ?? '',
      companyTaxId: storedBillingDetailsForm?.companyTaxId ?? '',
      country: storedBillingDetailsForm?.country ?? '',
      state: storedBillingDetailsForm?.state ?? '',
      city: storedBillingDetailsForm?.city ?? '',
      postalCode: storedBillingDetailsForm?.postalCode ?? '',
      addressLine1: storedBillingDetailsForm?.addressLine1 ?? '',
      addressLine2: storedBillingDetailsForm?.addressLine2 ?? '',
    },
    validationSchema: paymentValidationSchema.billing,
    onSubmit: (values) => {
      sessionStorage.setItem('billingDetailsForm', JSON.stringify(values))
      handleNext()
    },
  })

  const paymentDetailsFormik = useFormik({
    initialValues: {
      paymentMethod: storedPaymentMethod,
    },
    onSubmit: (values) => {
      if (!planId) return
      sessionStorage.setItem('paymentDetailsForm', JSON.stringify(values))

      const data = {
        pricing_plan_id: +planId,
        first_name: userDetailsFormik.values.firstName,
        last_name: userDetailsFormik.values.lastName,
        username: userDetailsFormik.values.username,
        email: userDetailsFormik.values.email,
        company_name: billingDetailsFormik.values.companyName,
        company_email: billingDetailsFormik.values.companyEmail,
        company_tax_id: billingDetailsFormik.values.companyTaxId,
        country: billingDetailsFormik.values.country,
        state: billingDetailsFormik.values.state,
        city: billingDetailsFormik.values.city,
        postal_code: billingDetailsFormik.values.postalCode,
        address_line_1: billingDetailsFormik.values.addressLine1,
        address_line_2: billingDetailsFormik.values.addressLine2,
      }

      if (
        Object.entries(data)
          .filter(([key, _]) => key !== 'address_line_2')
          .some(([_, value]) => !value)
      ) {
        sessionStorage.removeItem('paymentActiveStep')
        setActiveStep(1)
        return
      }

      mutate(
        {
          ...data,
          payment_method: values.paymentMethod,
          getToken: turnstile.getToken,
        },
        {
          onSuccess: (response) => {
            // Bank transfer is provisioned server-side and the details are
            // emailed; there is no Stripe redirect, so confirm it here instead
            // of leaving the user on a dead step.
            if (!isCardCheckout(response)) {
              setIsBankTransferModalOpen(true)
            }
          },
        },
      )
    },
  })

  const handleFormSubmit = async () => {
    switch (activeStep) {
      case 2:
        const userDetailsFormikErrors = await userDetailsFormik.validateForm()
        await userDetailsFormik.setTouched(
          Object.keys(userDetailsFormikErrors).reduce(
            (acc, key) => ({ ...acc, [key]: true }),
            {},
          ),
        )

        if (Object.keys(userDetailsFormikErrors).length > 0) {
          toast.error(t('pleaseFillAllTheRequiredFields'))
          return
        }

        userDetailsFormik.handleSubmit()
        break
      case 3:
        const billingDetailsFormikErrors =
          await billingDetailsFormik.validateForm()
        await billingDetailsFormik.setTouched(
          Object.keys(billingDetailsFormikErrors).reduce(
            (acc, key) => ({ ...acc, [key]: true }),
            {},
          ),
        )

        if (Object.keys(billingDetailsFormikErrors).length > 0) {
          toast.error(t('pleaseFillAllTheRequiredFields'))
          return
        }

        billingDetailsFormik.handleSubmit()
        break
      case 4:
        paymentDetailsFormik.handleSubmit()
        break
    }
  }

  useEffect(() => {
    if (paramsPlanId) {
      sessionStorage.setItem('planId', paramsPlanId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!paramsPlanId && !storedPlanId) {
    return <Navigate to='/' />
  }

  return (
    <div className='bg-white rounded-20px shadow-form max-w-[920px] w-full py-5 sm:py-[35px] px-5 sm:px-10 md:px-[85px] my-5'>
      {isBankTransferModalOpen && (
        <BankTransferSentModal
          onExit={() => setIsBankTransferModalOpen(false)}
          onGoToLogin={() => navigate('/login')}
        />
      )}
      <PaymentStepNumbers
        stepsConfig={paymentsStepsNumbersConfig(activeStep)}
      />
      <hr className='m-auto w-full border-[#E2E2E2]' />

      {activeStep === 1 && (
        <>
          {isPricingPlansLoading && (
            <div className='flex items-center justify-center my-[50px]'>
              <Spinner
                spinner='form'
                width='w-11'
                height='h-11'
                color='#0C75FF'
              />
            </div>
          )}
          {selectedPlan && !isPricingPlansLoading && (
            <ReviewPlan pricingPlan={selectedPlan} />
          )}
        </>
      )}
      {activeStep !== 1 && (
        <form className='my-[50px]'>
          {activeStep === 2 && (
            <PaymentUserDetailsForm formik={userDetailsFormik} />
          )}
          {activeStep === 3 && (
            <PaymentBillingDetailsForm formik={billingDetailsFormik} />
          )}
          {activeStep === 4 && (
            <>
              <PaymentMethodForm formik={paymentDetailsFormik} />
              <div ref={turnstile.containerRef} className='mt-5' />
              {turnstile.hasFailed && (
                <FormikErrorText errorText={t('humanVerificationFailed')} />
              )}
            </>
          )}
        </form>
      )}
      <hr className='m-auto w-full border-[#E2E2E2]' />
      <div className='flex justify-between items-center gap-5 flex-col-reverse sm:flex-row mt-5 sm:mt-[35px]'>
        {activeStep === 1 && (
          <a
            href={`${process.env.REACT_APP_WEBSITE_URL}/pricing?planId=${planId}`}
            className='text-primaryBlue hover:underline font-semibold text-sm duration-200 transition-all'
          >
            {t('changePlan')}
          </a>
        )}
        {activeStep !== 1 && <BackBtn onClickHandler={handleBack} />}

        <Button
          type='button'
          text={t(activeStep === 4 ? 'confirm' : 'next')}
          disabled={isLoading || (activeStep === 4 && !turnstile.isVerified)}
          onClickHandler={async () => {
            if (activeStep !== 1) {
              await handleFormSubmit()
              return
            }
            handleNext()
          }}
          width='sm:max-w-[142px] min-w-full sm:min-w-[auto] w-full'
        >
          {isLoading && <Spinner spinner='form' />}
        </Button>
      </div>
    </div>
  )
}

export default PaymentStepsForms
